import React, { useState } from 'react';
import { ExternalLink, BookOpen } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import data from '../../data/pathway15.json';

const SOURCES = Object.fromEntries(data.sources.map((s) => [s.id, s]));
export const getSource = (id) => SOURCES[id];

export const SourceButton = ({ label = 'Source & meaning', onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className="inline-flex items-center gap-1.5 text-xs font-bold text-teal-700 hover:text-teal-900 underline underline-offset-4 decoration-teal-300 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500">
    <BookOpen size={13} /> {label}
  </button>
);

const SourceRow = ({ s }) => (
  <li className="rounded-2xl border border-gray-200 bg-white p-4 space-y-1.5" data-testid={`p15-source-${s.id}`}>
    <p className="text-sm font-bold text-gray-900">{s.title}</p>
    <p className="text-xs text-gray-600">{s.publisher}{s.authority ? ` · ${s.authority}` : ''}</p>
    {s.reference_period && <p className="text-xs text-gray-700"><span className="font-semibold">Period:</span> {s.reference_period}</p>}
    {s.population && <p className="text-xs text-gray-700"><span className="font-semibold">Population:</span> {s.population}</p>}
    {s.locator && <p className="text-xs text-gray-700"><span className="font-semibold">Where:</span> {s.locator}</p>}
    {s.caveats?.length > 0 && <ul className="list-disc pl-4 text-xs text-gray-600 space-y-0.5">{s.caveats.map((c) => <li key={c}>{c}</li>)}</ul>}
    {s.url && <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-teal-700 hover:text-teal-900" data-testid={`p15-source-link-${s.id}`}>Open publisher source <ExternalLink size={12} /></a>}
  </li>
);

export const SourceDrawer = ({ open, onOpenChange, title, description, sourceIds = [], children }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-white" data-testid="p15-source-drawer">
      <SheetHeader className="text-left">
        <SheetTitle className="text-gray-900">{title}</SheetTitle>
        {description && <SheetDescription className="text-gray-600">{description}</SheetDescription>}
      </SheetHeader>
      <div className="mt-4 space-y-4 text-sm text-gray-800">
        {children}
        {sourceIds.length > 0 && <ul className="space-y-3">{sourceIds.map((id) => SOURCES[id] && <SourceRow key={id} s={SOURCES[id]} />)}</ul>}
      </div>
    </SheetContent>
  </Sheet>
);

export const useDrawer = () => {
  const [state, setState] = useState(null);
  return { drawer: state, open: (cfg) => setState(cfg), close: () => setState(null) };
};
